import React, { useState } from "react";
import AppModal from "./AppModal";
import { LoadingSpinner } from "./Loaders";
import { GoLiveSuccessSVG, GoLiveErrorSVG } from "./SvgIcons";
import "../../styles/confirmModal.less";

/**
 * ConfirmModal — single modal that handles prompt → loading → success/error.
 *
 * Props:
 *   show         {boolean}   — controls visibility
 *   onHide       {function}  — called after result is dismissed, or Cancel clicked
 *   title        {string}    — modal heading (all phases)
 *   message      {string}    — confirmation body text
 *   confirmLabel {string}    — label for the confirm button (default "Confirm")
 *   cancelLabel  {string}    — label for the cancel button (default "Cancel")
 *   onConfirm    {function}  — async function to run on confirm (should throw on failure)
 *   successMsg   {string}    — message shown on success
 *   errorMsg     {string}    — message shown on failure (overridden by thrown error message)
 *   maxWidth     {number}    — modal max-width in px (default 440)
 *   className    {string}    — extra class(es) appended to "confirm-modal"
 */
const ConfirmModal = ({
	show,
	onHide,
	title,
	message,
	body,
	confirmLabel = "Confirm",
	confirmDisabled = false,
	cancelLabel = "Cancel",
	onConfirm,
	successMsg = "Done.",
	errorMsg = "Something went wrong.",
	skipSuccess = false,
	maxWidth = 500,
	className = "",
}) => {
	// phase: "idle" | "loading" | "success" | "error"
	const [phase, setPhase] = useState("idle");
	const [resolvedMsg, setResolvedMsg] = useState("");

	const handleConfirm = async () => {
		setPhase("loading");
		try {
			await onConfirm();
			if (skipSuccess) {
				setPhase("idle");
				setResolvedMsg("");
				onHide();
			} else {
				setResolvedMsg(successMsg);
				setPhase("success");
			}
		} catch (err) {
			const msg = err?.data?.Message || err?.message || errorMsg;
			setResolvedMsg(msg);
			setPhase("error");
		}
	};

	const handleHide = () => {
		setPhase("idle");
		setResolvedMsg("");
		onHide();
	};

	const isDone = phase === "success" || phase === "error";
	const isLoading = phase === "loading";

	return (
		<AppModal
			show={show}
			onHide={handleHide}
			locked={isLoading}
			maxWidth={maxWidth}
			className={`confirm-modal${className ? ` ${className}` : ""}`}>
			{phase === "idle" && (
				<>
					{title && <h2 className="confirm-modal__title">{title}</h2>}
					{message && <p className="confirm-modal__message">{message}</p>}
					{body && <div className="confirm-modal__body">{body}</div>}
					<div className="confirm-modal__actions">
						<button
							type="button"
							className="primary-btn transparent-btn"
							onClick={handleHide}>
							{cancelLabel}
						</button>
						<button
							type="button"
							className="primary-btn"
							disabled={confirmDisabled}
							onClick={handleConfirm}>
							{confirmLabel}
						</button>
					</div>
				</>
			)}

			{isLoading && (
				<div className="confirm-modal__loading">
					<LoadingSpinner size="lg" center />
					<p className="confirm-modal__loading-text">Please wait…</p>
				</div>
			)}

			{phase === "success" && (
				<div className="confirm-modal__result">
					<div className="confirm-modal__result-icon"><GoLiveSuccessSVG /></div>
					<p className="confirm-modal__result-msg confirm-modal__result-msg--success">
						{resolvedMsg}
					</p>
					<div className="confirm-modal__actions">
						<button
							type="button"
							className="primary-btn white-btn"
							onClick={handleHide}>
							Close
						</button>
					</div>
				</div>
			)}

			{phase === "error" && (
				<div className="confirm-modal__result">
					<GoLiveErrorSVG />
					<p className="confirm-modal__result-msg confirm-modal__result-msg--error">
						{resolvedMsg}
					</p>
					<div className="confirm-modal__actions">
						<button
							type="button"
							className="primary-btn white-btn"
							onClick={handleHide}>
							Close
						</button>
					</div>
				</div>
			)}
		</AppModal>
	);
};

export default ConfirmModal;
